/**
 * Real LiveKit-backed handoff room controller.
 *
 * THIS IS THE ONLY FILE IN THE ENTIRE hostesa-app MONOREPO THAT MAY IMPORT
 * THE RAW livekit-client SDK. Consumers depend on the HandoffRoomConnection
 * interface from types.ts, not on this implementation.
 *
 * Error → canonical state mapping:
 *   ConnectionError (403 / bad token / room not found) → token_denied
 *   ConnectionError + "permission" / getUserMedia NotAllowedError → mic_denied
 *   ConnectionError (network, ICE)                    → failed
 *   RoomEvent.Disconnected (server close / eviction)  → disconnected
 */

import { ConnectionState, Room, type RoomConnectOptions, RoomEvent, Track } from 'livekit-client';

import type {
  CreateHandoffRoomOptions,
  HandoffMuteListener,
  HandoffRoomConnection,
  HandoffRoomState,
  HandoffStateListener,
} from './types.js';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Map a raw LiveKit ConnectionState (post-connect) to canonical state. */
function mapConnectionState(cs: ConnectionState): HandoffRoomState {
  switch (cs) {
    case ConnectionState.Connecting:
    case ConnectionState.Reconnecting:
      return 'connecting';
    case ConnectionState.Connected:
      return 'connected';
    case ConnectionState.Disconnected:
      return 'disconnected';
    default:
      return 'failed';
  }
}

/**
 * Attempt to classify a caught error during connect() into a canonical state.
 * LiveKit throws generic Error instances; we inspect the message string.
 */
function classifyConnectError(err: unknown): HandoffRoomState {
  const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();

  // Microphone permission denied (browser getUserMedia)
  if (
    msg.includes('notallowederror') ||
    msg.includes('permission denied') ||
    msg.includes('permission_denied') ||
    msg.includes('microphone') ||
    msg.includes('notfounderror')
  ) {
    return 'mic_denied';
  }

  // Token rejected / room not found / expired
  if (
    msg.includes('403') ||
    msg.includes('401') ||
    msg.includes('invalid token') ||
    msg.includes('token') ||
    msg.includes('not found') ||
    msg.includes('room') ||
    msg.includes('expired')
  ) {
    return 'token_denied';
  }

  // Anything else (ICE, DTLS, network, timeout)
  return 'failed';
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

class HandoffRoomImpl implements HandoffRoomConnection {
  private readonly _room: Room;
  private readonly _token: string;
  private readonly _serverUrl: string;

  private _state: HandoffRoomState = 'idle';
  private _muted = false;

  private readonly _stateListeners = new Set<HandoffStateListener>();
  private readonly _muteListeners = new Set<HandoffMuteListener>();

  constructor(options: CreateHandoffRoomOptions) {
    this._token = options.token;
    this._serverUrl = options.serverUrl;

    this._room = new Room({
      // Disable adaptive stream by default — let the backend decide quality
      adaptiveStream: false,
      // Staff participant only publishes audio, no video needed
      publishDefaults: {
        videoSimulcastLayers: [],
      },
      audioCaptureDefaults: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    this._bindRoomEvents();
  }

  // ------------------------------------------------------------------
  // HandoffRoomConnection interface
  // ------------------------------------------------------------------

  get state(): HandoffRoomState {
    return this._state;
  }

  get muted(): boolean {
    return this._muted;
  }

  async connect(): Promise<void> {
    if (this._state !== 'idle' && this._state !== 'disconnected') {
      return;
    }

    this._setState('connecting');

    try {
      const opts: RoomConnectOptions = {
        autoSubscribe: true,
      };
      await this._room.connect(this._serverUrl, this._token, opts);
      // The RoomEvent.ConnectionStateChanged handler will transition to 'connected'
    } catch (err) {
      const canonical = classifyConnectError(err);
      this._setState(canonical);
      console.error('[HandoffRoom] connect error → state:', canonical, err);
    }
  }

  mute(): void {
    if (!this._muted) {
      this._setMuted(true);
      void this._room.localParticipant.setMicrophoneEnabled(false);
    }
  }

  unmute(): void {
    if (this._muted) {
      this._setMuted(false);
      void this._room.localParticipant.setMicrophoneEnabled(true);
    }
  }

  leave(): void {
    void this._room.disconnect();
    // RoomEvent.Disconnected will set state to 'disconnected'
  }

  onStateChange(listener: HandoffStateListener): () => void {
    this._stateListeners.add(listener);
    return () => {
      this._stateListeners.delete(listener);
    };
  }

  onMuteChange(listener: HandoffMuteListener): () => void {
    this._muteListeners.add(listener);
    return () => {
      this._muteListeners.delete(listener);
    };
  }

  destroy(): void {
    this._stateListeners.clear();
    this._muteListeners.clear();
    void this._room.disconnect(true);
  }

  // ------------------------------------------------------------------
  // Private
  // ------------------------------------------------------------------

  private _setState(next: HandoffRoomState): void {
    if (this._state === next) return;
    this._state = next;
    for (const l of this._stateListeners) {
      l(next);
    }
  }

  private _setMuted(next: boolean): void {
    if (this._muted === next) return;
    this._muted = next;
    for (const l of this._muteListeners) {
      l(next);
    }
  }

  private _bindRoomEvents(): void {
    this._room.on(RoomEvent.ConnectionStateChanged, (cs: ConnectionState) => {
      // Only map if we are still in an expected in-flight state
      if (
        this._state === 'connecting' ||
        this._state === 'connected' ||
        this._state === 'disconnected'
      ) {
        this._setState(mapConnectionState(cs));
      }
    });

    this._room.on(RoomEvent.Disconnected, () => {
      this._setState('disconnected');
    });

    this._room.on(RoomEvent.LocalTrackUnpublished, () => {
      // Microphone track removed — reflect muted state in case the track was killed externally
      const micPub = this._room.localParticipant.getTrackPublication(Track.Source.Microphone);
      if (!micPub?.track) {
        this._setMuted(true);
      }
    });
  }
}

// ---------------------------------------------------------------------------
// Factory (public)
// ---------------------------------------------------------------------------

/**
 * Create a real LiveKit-backed handoff room connection.
 *
 * The returned controller is idle; call `.connect()` to begin the session.
 *
 * @example
 * ```ts
 * const room = createHandoffRoom({ token, serverUrl });
 * const unsub = room.onStateChange(s => console.log('state:', s));
 * await room.connect();
 * // ...later
 * room.leave();
 * room.destroy();
 * unsub();
 * ```
 */
export function createHandoffRoom(options: CreateHandoffRoomOptions): HandoffRoomConnection {
  return new HandoffRoomImpl(options);
}
